import React, { useEffect, useState } from "react";
import { RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCPopover, RCCSwitch, RCCTextArea } from "react-common-components";
import SQLParser from "sql-parser";
import Audit from "src/components/Audit";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import { bindTypes, columnsTypes, IReport, ITab, IWdtba003, IWdtba009 } from "../../interface";

const TabGeneralData: React.FC<ITab> = ({ 
	report,
	binds,
	setBinds,
	columns,
	setColumns,
	register,
	errors,
	setValue,
	unregister
}) => {
	const { translate} = useTranslation();
	const { addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	
	const [data,setData] = useState<Omit<IReport,"columns"|"binds">>(report);
	const sessionBinds = ["EFA001_ID","EFA002_ID","EFA005_ID"];

	const processBinds = (sql:string) :IWdtba009[] => {
		const regex = /:\s*([\dA-Za-z_]+)(?=([^'\\]*(\\.|'([^'\\]*\\.)*[^'\\]*'))*[^']*$)/gm;
		const matches = (sql.match(regex) || [])
			.map((elem) => elem.substring(1).toUpperCase())
			.filter((elem, pos, arr) => arr.indexOf(elem) === pos)
			.map((elem, pos) => ({ id:v4(), wda009Id:0,wda009Bind: elem, wda009Tipo: bindTypes.FREE, wda009Valordefault: "", wda009Sqllov: "", wda009Ordem: pos } as IWdtba009))
			.filter((elem) => sessionBinds.indexOf(elem.wda009Bind) === -1)
			.sort((a, b) =>  a.wda009Ordem - b.wda009Ordem);
		return matches;
		
	};

	const processColumns = (sql:string):IWdtba003[] => {
		let sqltext = sql.replace(/(\r\n|\n|\r)/gm, "");
		sqltext = sqltext.replace(/:/gm,"_BIND_");
		sqltext = sqltext.replace(/(\|\|)/gm,"+");

		let parsed = SQLParser.lexer.tokenize(sqltext);

		if (parsed[0][0] !== "SELECT") {
			throw new Error(`${translate("Esperava um \"SELECT\" e encontrou \"{{value}}\"",{value:parsed[0][1]})}.`);
		}

		// Faz a busca pelas colunas do SELECT
		let openedSelects = 0;
		let posi = 0;
		let posf = 0;
		let pos = 0;
		let token = parsed[pos];
		while (token) {
			if (token[0] === "EOF") {
				return [];
			} if (token[0] === "SELECT") {
				openedSelects +=1;
				if (openedSelects === 1) {
					posi = pos + 1;
				}
			} else if (token[0] === "DISTINCT") {
				if (openedSelects === 1) {
					posi = pos + 1;
				}
			} else if (token[0] === "FROM") {
				openedSelects -=1;
				if (!openedSelects) {
					posf = pos - posi;
					break;
				}
			}
			pos+=1;
			token = parsed[pos];
		}
		parsed = parsed.splice(posi, posf);

		const result = [];
		while (parsed.length) {
			let openedBrackets = 0;
			let openedCases = 0;
			for (pos = 0; pos < parsed.length; pos+=1) {
				token = parsed[pos];
				if (token[0] === "LEFT_PAREN") {
					openedBrackets+=1;
				} else if (token[0] === "RIGHT_PAREN") {
					openedBrackets-=1;
				} else if (token[0] === "LITERAL" && token[1].toUpperCase() === "CASE") {
					openedCases+=1;
				} else if (token[0] === "LITERAL" && token[1].toUpperCase() === "END") {
					openedCases-=1;
				} else if (token[0] === "SEPARATOR" && openedBrackets === 0 && openedCases === 0) {
					break;
				}
			}
			// Separa os tokens que formam a coluna encontrada dos demais tokens
			const tokens = parsed.splice(0, pos);
			// Remove o separador que vai ficar como primeiro elemento
			parsed.shift();
			// Monta o objeto da coluna
			const coluna = { 
				id:v4(),
				wda003Id:0,
				wda003Nomecoluna: "", 
				wda003Label: "", 
				wda003Tipocoluna: columnsTypes.ALFA_NUMERIC,
				wda003Ordem:pos,
				wda003Tamanho:1
			} as IWdtba003;

			// Verifica se o último token da coluna é STAR, se for, ignora a coluna pois ela deve ter o formato '*' ou 'TABLE.*'
			pos = tokens.length - 1;
			if (tokens[pos][0] !== "STAR") {
				if (tokens.length === 1) { // Apenas um token: VALOR e ALIAS serão iguais
					if (tokens[0][0] === "LITERAL") {
						coluna.wda003Nomecoluna = tokens[0][1];
						coluna.wda003Label = tokens[0][1];
					} else if (tokens[0][0] === "STRING") {
						coluna.wda003Nomecoluna = `'${tokens[0][1]}'`;
						coluna.wda003Label = tokens[0][1];
					} else if (tokens[0][0] === "NUMBER") {
						coluna.wda003Nomecoluna = tokens[0][1];
						coluna.wda003Label = tokens[0][1];
						coluna.wda003Tipocoluna = columnsTypes.NUMERIC;
					}
				} else { // Mais de um token: O último token será o ALIAS
					if ((tokens.length === 2) || // Dois tokens: VALOR com um ALIAS
							(tokens.length === 3 && tokens[1][0] === "AS")) { // Três tokens com AS intermediário: VALOR com um ALIAS após o AS
						if (tokens[0][0] === "NUMBER") {
							coluna.wda003Tipocoluna = columnsTypes.NUMERIC;
						}
					}
					if (tokens[pos][0] === "LITERAL") {
						coluna.wda003Nomecoluna = tokens[pos][1];
						coluna.wda003Label = tokens[pos][1];
					} else if (tokens[pos][0] === "DBLSTRING") {
						coluna.wda003Nomecoluna = `"${tokens[pos][1]}"`;
						coluna.wda003Label = tokens[pos][1];
					}
				}
				result.push(coluna);
			} 	
		}
		return result;
	};

	const compareColumns = (newCols:IWdtba003[]) => {
		
		const cols = [...columns];
		if (cols.length) {
			// Verifica se a quantidade de colunas cadastradas difere da quantidade de colunas encontradas, se diferente, retorna true para pedir reprocessamento
			if (cols.length !== newCols.length) {
				return true;
			}
			// Verifica se as colunas cadastradas diferem das colunas encontradas, se houver ao menos uma diferente, retorna true para pedir reprocessamento			
			for (let i = 0; i < cols.length; i+=1) {
				const exists = newCols.some((newCol) => cols[i].wda003Nomecoluna === newCol.wda003Nomecoluna);

				if (!exists) {
					return true;
				}
			}
		}

		// Não deve solicitar reprocessamento
		return false;
	};

	const saveColumns = (newCols:IWdtba003[]) => {

		let cols = [...columns] as IWdtba003[];
		cols = cols.filter((elem) => newCols.some((x) => x.wda003Nomecoluna === elem.wda003Nomecoluna));
		cols = [...cols,...newCols.filter((elem) => !cols.some((x) => x.wda003Nomecoluna === elem.wda003Nomecoluna))];
		unregister("columns");
		setValue("columns",cols);
		setColumns(cols);
	};

	const saveBinds = (newBinds:IWdtba009[]) => {
		let updateBinds = [...binds];
		updateBinds = updateBinds.filter((elem) => newBinds.some((x) => x.wda009Bind === elem.wda009Bind));
		updateBinds = [...updateBinds,...newBinds.filter((elem) => !updateBinds.some((x) => x.wda009Bind === elem.wda009Bind))];
		unregister("binds");
		setValue("binds",updateBinds);
		setBinds(updateBinds);
	};

	const parseSql = (sql:any) => {
		try {
			const newColumns = processColumns(sql.target.value);
			const newBinds = processBinds(sql.target.value);
			if (newColumns){
				if(compareColumns(newColumns)){
					confirm(
						translate("Reprocesso de colunas"),
						`${translate("A consulta foi alterada e novas colunas foram encontradas")}. ${translate("Deseja reprocessar as colunas existentes")} ?`,
						() => {
							saveColumns(newColumns);
						},
						(err) => {
							addToast({
								color: "red",
								description: translate("Erro ao reprocesar colunas"),
								title: translate("Erro"),
								details: {
									header:"",
									text: err as string
								}
							});
						},
						true
					);
				}else{
					saveColumns(newColumns);
				}
			}
			if(newBinds){
				saveBinds(newBinds);
			}

		} catch (error) {
			addToast({
				color: "red",
				description: translate("Erro ao identificar comando sql"),
				title: translate("Erro"),
				details: {
					header:"",
					text: error as string
				}
			});
		}
		
	};
	
	useEffect(() => {
		setData(report);
	}, [report]);

	return(
		<RCCGrid container item 
			justifyContent="center">
			<RCCGrid container item 
				xs={12} 
				lg={10}
			>
				<RCCGrid
					container
					item
					justifyContent="flex-end"
				>
					<RCCGrid item>
						<Audit 
							data={report} 
							type="pop" 
							position="left"
						/> 
					</RCCGrid>
				</RCCGrid>
				<RCCGrid container item>		
					<RCCInput 
						name="wda002Titulo"
						register={register}
						label={translate("Título")}
						defaultValue={data.wda002Titulo}
						error={errors?.wda002Titulo}
						maxLength={200}
						required
					/>
					<input 
						{...register("wda002Id")} 
						name="wda002Id"
						value={data.wda002Id||0}
						type="hidden"
					/>	
				</RCCGrid>
				<RCCGrid container item>		
					<RCCInput 
						name="wda002Tituloabrev"
						register={register}
						label={translate("Titulo abreviado")}
						defaultValue={data.wda002Tituloabrev}
						error={errors?.wda002Tituloabrev}
						maxLength={200}
					/>	
				</RCCGrid>
				<RCCGrid container item>		
					<RCCInput 
						name="wda002Agrupador"
						register={register}
						label={translate("Agrupador")}
						defaultValue={data.wda002Agrupador}
						error={errors?.wda002Agrupador}
						maxLength={40}
					/>
				</RCCGrid>
				<RCCGrid container item>
					<RCCSwitch
						name="wda002Ativo"
						label={translate("Ativo")}
						register={register}
						error={errors?.wda002Ativo}
						defaultChecked={data.wda002Ativo === undefined ? true: data.wda002Ativo}
					/>
					<RCCSwitch
						name="wda002Interno"
						label={translate("Uso interno")}
						register={register}
						error={errors?.wda002Interno}
						defaultChecked={data.wda002Interno === undefined ? true: data.wda002Interno}
					/>
					
				</RCCGrid>
				<RCCGrid container item wrap="nowrap">
					<RCCTextArea 
						name="wda002Sqlquery"
						label={translate("Sql")}
						required
						defaultValue={data.wda002Sqlquery}
						error={errors?.wda002Sqlquery}
						register={register}
						rows={9}
						onBlurCapture={parseSql}
					/>
					<RCCPopover
						header={{title:translate("Binds reservadas")}}
						body={{text:`${translate("As variáveis \"EFA001_ID\", \"EFA002_ID\", \"EFA005_ID\", são variáveis reservadas do sistema")}.`
						}}
					>
						<RCCIcon
							name="infoCircle"
						/>
					</RCCPopover>
				</RCCGrid>
			</RCCGrid>
		</RCCGrid>
	);
};
export default TabGeneralData;
